import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { showAppToast } from '../../Utils/appToast';
import styles from './checkoutSidebar.module.css';
import BackIcon from '../../Assets/images/back.svg';
import Deleteicon from '../../Assets/images/delete.svg';
import EditIcon from '../../Assets/images/EditIcon.svg';
import VoucherScanIcon from '../Icons/VoucherScanIcon';
import QuantityCircleAddIcon from '../Icons/QuantityCircleAddIcon';
import QuantityCircleRemoveIcon from '../Icons/QuantityCircleRemoveIcon';
import TrashIcon from '../Icons/TrashIcon';
import VoucherVerifySuccessIcon from '../Icons/VoucherVerifySuccessIcon';
import CustomDropdown from '../CustomerDetailsModal/CustomDropdown';
import CustomerDetailsModal from '../CustomerDetailsModal/CustomerDetailsModal';
import {
  enrichCustomerWithCheckoutPasswordFlag,
  fetchAndStoreCheckoutCustomerPasswordFlag,
} from '../../Utils/customerPasswordSession';
import CustomerSelector from '../RightSidebar/CustomerSelector';
import { useGlobalContext } from '../../Context/GlobalContext';
import PaymentPage from '../RightSidebar/PaymentPage';
import ConfirmationPage from '../RightSidebar/ConfirmationPage';
import { getProductsList, getVoucherProductsList, postProductPayment, postVoucherVerify, getSalesList, getSelectedOutlet, checkOutletAvailability } from '../../APICalling/APICalling';
import { roundCashAmount, isCheckoutVoucherAddVisibleHost, extractOutletFromApiResponse } from '../../Utils/utils';
import { buildMembershipIdPaymentFields } from '../../Utils/customerSearchUtils';
import {
  refreshCustomerCreditDetailsFromUser,
  membershipClientToTopupCustomer,
  fetchMembershipClientCreditPatch,
  computeRemainingCheckoutAmount,
} from '../../Utils/customerCreditRefresh';
import { getOpenMembershipRecoveryIds } from '../../Utils/openMembershipSale';
import CrossIcon from '../../Assets/images/cross.svg';
import dropdownStyles from '../DropDown/PriceAdjustmentDropdowns/Style.module.css';
import {
  buildVoucherOptionsFromList,
  createEmptyVoucherCodes,
  normalizeVoucherCodes,
  voucherVerifySlotKey,
  remapVoucherVerifyForQtyChange,
  isVoucherVerifyResponseOk,
  getVoucherVerifySuccessMessage,
  getVoucherVerifyErrorMessage,
  extractServiceIdsFromBooking,
  isCartVoucherSerialPresent,
  getVoucherFromVerifyResponse,
  findVoucherCatalogProduct,
  buildOptionFromVoucherCatalogProduct,
  isEditableInputFocused,
} from './voucherSaleHelpers';
import { PAYMENT_IN_PROGRESS_MESSAGE } from '../../Utils/Constants/errorMessages';
import logger from '../../Utils/logger';

const PRODUCT_MEMBER_DISCOUNT_MULTIPLIER = 0.8;
// Show voucher add only on allowlisted hosts (localhost / QA); hide on staging & everywhere else
const hideCheckoutVoucherAddOnStaging = !isCheckoutVoucherAddVisibleHost();

const countVerifiedVoucherSlots = (item, voucherVerifyByKey = {}) => {
  if (!item?.isVoucher || !item.lineId) return 0;

  const codes = normalizeVoucherCodes(item.voucherCodes, item.qty);
  return codes.reduce((count, _code, codeIdx) => {
    const key = voucherVerifySlotKey(item.lineId, codeIdx);
    return voucherVerifyByKey[key]?.status === 'success' ? count + 1 : count;
  }, 0);
};

const getItemLineAmount = (item, voucherVerifyByKey = null) => {
  const unitPrice = Number(item.price) || 0;

  if (item.isVoucher) {
    const verifiedQty =
      voucherVerifyByKey && item.lineId
        ? countVerifiedVoucherSlots(item, voucherVerifyByKey)
        : 0;
    return unitPrice * verifiedQty;
  }

  const qty = Number(item.qty) || 0;
  const lineAmount = unitPrice * qty;
  const isCatalogPrice = unitPrice === Number(item.originalPrice);

  if (item.isMemberRateApplied && isCatalogPrice) {
    return lineAmount * PRODUCT_MEMBER_DISCOUNT_MULTIPLIER;
  }

  return lineAmount;
};

/** Line price shown in UI; vouchers use full qty, not verify status. */
const getItemDisplayLineAmount = (item) => {
  const unitPrice = Number(item.price) || 0;
  const qty = Number(item.qty) || 0;

  if (item.isVoucher) {
    return unitPrice * qty;
  }

  return getItemLineAmount(item);
};

const getItemMemberDiscountAmount = (item) => {
  if (item.isVoucher || !item.isMemberRateApplied) return 0;

  const unitPrice = Number(item.price) || 0;
  if (unitPrice !== Number(item.originalPrice)) return 0;

  const lineAmount = unitPrice * (Number(item.qty) || 0);
  return lineAmount * (1 - PRODUCT_MEMBER_DISCOUNT_MULTIPLIER);
};

const canMergeCartLines = (existing, productId, unitPrice, applyMemberRate) =>
  !existing.isVoucher &&
  existing.productId === productId &&
  Boolean(existing.isMemberRateApplied) === applyMemberRate &&
  Number(existing.price) === unitPrice &&
  Number(existing.originalPrice) === unitPrice &&
  !existing.editing;

const createCartLineId = () =>
  `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const extractSaleIdFromPaymentResponse = (response, fallbackId = null) => {
  const data = response?.data?.data || response?.data || {};
  const candidates = [
    data?.booking_id,
    data?.sale_id,
    data?.id,
    response?.data?.booking_id,
    response?.data?.sale_id,
    fallbackId,
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return String(candidate).replace('#', '');
    }
  }

  return null;
};

const fetchLatestProductSale = async (customerId, saleType = 'product') => {
  const salesResponse = await getSalesList({
    pagination: 1,
    limit: 1,
    customer: customerId,
    sort_by: 'created_at',
    sort_order: 'desc',
    type: saleType,
  });

  return salesResponse?.data?.data?.list?.bookings?.[0] || null;
};

/** Maps checkout cart lines to payment API items (product vs voucher). */
const buildProductPaymentApiItems = (cartItems, customerName = '', voucherVerifyByKey = {}) => {
  const apiItems = [];
  let productItemNumber = 0;

  cartItems.forEach((item) => {
    if (item.isVoucher) {
      const codes = normalizeVoucherCodes(item.voucherCodes, item.qty);
      codes.forEach((code, codeIdx) => {
        const serial = String(code || '').trim();
        if (!serial) return;

        const verifyKey = item.lineId ? voucherVerifySlotKey(item.lineId, codeIdx) : '';
        if (verifyKey && voucherVerifyByKey[verifyKey]?.status !== 'success') return;

        apiItems.push({
          item_type: 'voucher',
          serial_number: serial,
        });
      });
      return;
    }

    productItemNumber += 1;
    const baseItem = {
      item_type: 'product',
      product: item.productId || 1,
      product_name: item.name || '',
      price: item.originalPrice.toString(),
      quantity: item.qty.toString(),
      customer_name: customerName || '',
      item_number: productItemNumber,
      reason: item.reason || '',
      membership: item.isMemberRateApplied ? 1 : 0,
    };

    const isCatalogPrice = Number(item.price) === Number(item.originalPrice);
    if (item.isMemberRateApplied && isCatalogPrice) {
      baseItem.change_price = (
        Number(item.originalPrice) * PRODUCT_MEMBER_DISCOUNT_MULTIPLIER
      ).toString();
    } else if (!isCatalogPrice) {
      baseItem.change_price = item.price.toString();
    }

    apiItems.push(baseItem);
  });

  return apiItems;
};

const CheckoutSidebar = ({ open, onClose, onSaleCompleted }) => {
  const { isMembershipApplied, setIsMembershipApplied, setServiceTotal, isCustomerSelected, customerData: globalCustomerData, setIsCustomerSelected, paymentHistory, setPaymentHistory, amountDue, setAmountDue, amountToCharge, setAmountToCharge, currentPaymentMethod, currentPaymentMethodId, setCurrentPaymentMethod, setCurrentPaymentMethodId, setCustomerCreditDetails, loggedOutletID, selectedOutlet, selectedOutletData, loggedUser, voucherDetail } = useGlobalContext();
  const [items, setItems] = useState([]); // {name, price, qty}
  const [step, setStep] = useState('checkout');
  const [productOptions, setProductOptions] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [note, setNote] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [bookingDataFromAPI, setBookingDataFromAPI] = useState(null);
  const [customerDataFromAPI, setCustomerDataFromAPI] = useState(null);
  const [bookingType, setBookingType] = useState(null);
  const [isCustomerSelectedLocal, setIsCustomerSelectedLocal] = useState(false);
  const [customerApiError, setCustomerApiError] = useState("");
  const [localOutletData, setLocalOutletData] = useState(null);
  const [productsData, setProductsData] = useState([]); // Store full product data from API
  const [voucherProductsData, setVoucherProductsData] = useState([]);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(false);
  const [vouchersError, setVouchersError] = useState('');
  const [voucherVerifyByKey, setVoucherVerifyByKey] = useState({});
  const [isCheckoutTopupOpen, setIsCheckoutTopupOpen] = useState(false);
  const [checkoutMembershipFlowType, setCheckoutMembershipFlowType] = useState('topup');
  const [checkoutTopupRefreshKey, setCheckoutTopupRefreshKey] = useState(0);
  const [checkoutRecoveryMembershipSale, setCheckoutRecoveryMembershipSale] = useState(null);
  const [othersMembershipClient, setOthersMembershipClient] = useState(null);
  const [checkoutTopupCustomer, setCheckoutTopupCustomer] = useState(null);
  const [isOthersMemberTopupFlow, setIsOthersMemberTopupFlow] = useState(false);
  const [showPaymentInProgressNotice, setShowPaymentInProgressNotice] = useState(false);
  const contentRef = React.useRef(null);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const checkoutPaymentMethodBeforeTopupRef = React.useRef({ method: '', id: null });
  const checkoutAmountsBeforeTopupRef = React.useRef({ amountDue: null, amountToCharge: null });
  const checkoutStepBeforeTopupRef = React.useRef('confirmation');
  const checkoutTopupTargetCustomerRef = React.useRef(null);
  const prevMembershipAppliedRef = React.useRef(isMembershipApplied);
  const productDiscountedSubtotal = items.reduce(
    (sum, item) => sum + getItemLineAmount(item, voucherVerifyByKey),
    0
  );
  const productGstAmount = items.reduce((sum, item) => {
    const discountedLineAmount = getItemLineAmount(item, voucherVerifyByKey);
    const gstRate = Number(item.gst);

    return sum + (Number.isFinite(gstRate) ? (discountedLineAmount * gstRate) / 100 : 0);
  }, 0);
  const productGrandTotal = productDiscountedSubtotal + productGstAmount;

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    voucherProductsDataRef.current = voucherProductsData;
  }, [voucherProductsData]);
  const isCheckoutLocked = Boolean(bookingDataFromAPI?.booking_id);
  const hasProductItems = items.some((item) => !item.isVoucher);
  const hasVoucherItems = items.some((item) => item.isVoucher);
  const canAddProducts = !hasVoucherItems;
  const canAddVouchers = !hasProductItems;

  const showPaymentInProgress = () => {
    if (!isCheckoutLocked) return;
    setShowPaymentInProgressNotice(true);
  };

  const lockedEditClickProps = isCheckoutLocked
    ? { onClick: showPaymentInProgress }
    : {};

  useEffect(() => {
    if (!isCheckoutLocked) {
      setShowPaymentInProgressNotice(false);
    }
  }, [isCheckoutLocked]);

  useEffect(() => {
    if (!isCheckoutLocked) return;
    setItems((prev) => {
      const hasEditing = prev.some((item) => item.editing);
      if (!hasEditing) return prev;
      return prev.map((item) => (item.editing ? { ...item, editing: false } : item));
    });
  }, [isCheckoutLocked]);

  useEffect(() => {
    if (!open) {
      setIsCheckoutTopupOpen(false);
      checkoutTopupTargetCustomerRef.current = null;
      setCheckoutTopupCustomer(null);
      setOthersMembershipClient(null);
    }
  }, [open]);

  const resolveTopupUserId = () => {
    const targetUserId =
      checkoutTopupTargetCustomerRef.current?.user_id ?? checkoutTopupCustomer?.id;
    if (targetUserId != null && targetUserId !== '') {
      return targetUserId;
    }
    return globalCustomerData?.id || bookingDataFromAPI?.user_id || null;
  };

  const buildTopupCheckoutCustomer = () => {
    const targetCustomer = membershipClientToTopupCustomer(
      checkoutTopupTargetCustomerRef.current
    );
    if (targetCustomer) {
      return enrichCustomerWithCheckoutPasswordFlag(targetCustomer);
    }

    const base =
      globalCustomerData ||
      customerDataFromAPI ||
      (bookingDataFromAPI
        ? {
            id: bookingDataFromAPI?.user_id ?? bookingDataFromAPI?.id ?? null,
            name: bookingDataFromAPI?.user_name ?? bookingDataFromAPI?.name ?? '',
            contact_number:
              bookingDataFromAPI?.contact_number ?? bookingDataFromAPI?.user_number ?? '',
            email: bookingDataFromAPI?.user_email ?? bookingDataFromAPI?.email ?? '',
          }
        : null);
    return enrichCustomerWithCheckoutPasswordFlag(base);
  };

  const openCheckoutMembershipFlow = async (flowType = 'topup', membershipClient = null) => {
    checkoutStepBeforeTopupRef.current = step || 'confirmation';
    checkoutTopupTargetCustomerRef.current = membershipClient || null;
    setIsOthersMemberTopupFlow(Boolean(membershipClient?.user_id));
    const mappedTopupCustomer = membershipClient
      ? enrichCustomerWithCheckoutPasswordFlag(
          membershipClientToTopupCustomer(membershipClient)
        )
      : null;
    setCheckoutTopupCustomer(mappedTopupCustomer);
    if (membershipClient && !mappedTopupCustomer) {
      showAppToast('error', 'Could not resolve the selected member for top-up.');
      return;
    }

    checkoutPaymentMethodBeforeTopupRef.current = {
      method: currentPaymentMethod,
      id: currentPaymentMethodId,
    };
    checkoutAmountsBeforeTopupRef.current = {
      amountDue: amountDue,
      amountToCharge: amountToCharge,
    };
    setCheckoutMembershipFlowType(flowType);
    const userId =
      membershipClient?.user_id ?? mappedTopupCustomer?.id ?? resolveTopupUserId();
    if (userId) {
      await fetchAndStoreCheckoutCustomerPasswordFlag(userId);
    }
    if (isCheckoutTopupOpen) {
      setIsCheckoutTopupOpen(false);
      window.setTimeout(() => setIsCheckoutTopupOpen(true), 0);
      return;
    }
    setIsCheckoutTopupOpen(true);
  };

  const openMembershipSaleRecovery = (sale) => {
    const { transactionId, membershipId } = getOpenMembershipRecoveryIds(sale);
    if (!transactionId || !membershipId) {
      showAppToast('error', 'Could not find the open membership sale.');
      return;
    }
    checkoutStepBeforeTopupRef.current = step || 'confirmation';
    checkoutPaymentMethodBeforeTopupRef.current = {
      method: currentPaymentMethod,
      id: currentPaymentMethodId,
    };
    checkoutAmountsBeforeTopupRef.current = {
      amountDue: amountDue,
      amountToCharge: amountToCharge,
    };
    checkoutTopupTargetCustomerRef.current = null;
    setIsOthersMemberTopupFlow(false);
    setCheckoutTopupCustomer(buildTopupCheckoutCustomer());
    setCheckoutMembershipFlowType('topup');
    setCheckoutRecoveryMembershipSale(sale);
    setIsCheckoutTopupOpen(true);
  };

  const restoreCheckoutPaymentMethodAfterTopup = () => {
    const { method, id } = checkoutPaymentMethodBeforeTopupRef.current;
    setCurrentPaymentMethod(method || '');
    setCurrentPaymentMethodId(id ?? null);
  };
  const voucherScanInputRefs = useRef({});
  const skipVoucherVerifyBlurRef = useRef({});
  const pendingVoucherFocusRef = useRef(null);
  const globalVoucherScanBufferRef = useRef('');
  const globalVoucherScanProcessingRef = useRef(false);
  const voucherProductsDataRef = useRef([]);

  const fetchAllOutletProducts = async (outletId) => {
    const allProducts = [];
    let page = 1;
    let lastPage = 1;
    let safetyCounter = 0;

    do {
      const resp = await getProductsList({ pagination: 1, page, outlet: outletId });
      const pageProducts = resp?.data?.data?.list?.service || [];
      const paginationInfo = resp?.data?.data?.list?.pagination || resp?.data?.data?.pagination || {};

      if (Array.isArray(pageProducts) && pageProducts.length > 0) {
        allProducts.push(...pageProducts);
      }

      const apiCurrentPage = Number(paginationInfo.currentPage || page);
      const apiLastPage = Number(paginationInfo.lastPage || apiCurrentPage || 1);

      page = apiCurrentPage + 1;
      lastPage = apiLastPage;
      safetyCounter += 1;
    } while (page <= lastPage && safetyCounter < 50);

    return allProducts;
  };

  const fetchAllVoucherProducts = async () => {
    const all = [];
    let page = 1;
    let lastPage = 1;
    let safetyCounter = 0;

    do {
      const resp = await getVoucherProductsList({ pagination: 1, page });
      const pageProducts =
        resp?.data?.data?.list?.products ||
        resp?.data?.list?.products ||
        [];
      const paginationInfo =
        resp?.data?.data?.list?.pagination ||
        resp?.data?.data?.pagination ||
        resp?.data?.list?.pagination ||
        {};

      if (Array.isArray(pageProducts) && pageProducts.length > 0) {
        all.push(...pageProducts);
      }

      const apiCurrentPage = Number(paginationInfo.currentPage || page);
      const apiLastPage = Number(paginationInfo.lastPage || apiCurrentPage || 1);
      page = apiCurrentPage + 1;
      lastPage = apiLastPage;
      safetyCounter += 1;
    } while (page <= lastPage && safetyCounter < 50);

    return all;
  };

  useEffect(() => {
    if (!open || step !== 'checkout') return;
    const focusCustomerInput = () => {
      const input = document.querySelector('input[type="search"][placeholder="Search or create customer"]');
      if (input) {
        input.focus();
        return true;
      }
      return false;
    };
    focusCustomerInput();
    const t1 = setTimeout(focusCustomerInput, 50);
    const t2 = setTimeout(focusCustomerInput, 150);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open, step]);

  const buildOptionsFromProductsApi = (serviceList = [], outletId = null) => {
    // Store full product data
    setProductsData(serviceList);
    
    // Filter products to only show those available in the logged-in outlet
    const filteredProducts = serviceList.filter((p) => {
      // If no outlet ID, don't filter (shouldn't happen, but safety check)
      if (!outletId) return false;
      
      // Check if product has availability for this outlet
      if (Array.isArray(p.outlet_availabilities) && p.outlet_availabilities.length > 0) {
        // Convert both to numbers for comparison to handle string/number mismatches
        const outletIdNum = Number(outletId);
        const outletAvailability = p.outlet_availabilities.find(oa => Number(oa.outlet_id) === outletIdNum);
        // Only show if found and available === "Yes"
        if (outletAvailability) {
          return outletAvailability.available === "Yes";
        }
        // If outlet_availabilities exist but none match this outlet, don't show the product
        return false;
      }
      
      // If no outlet_availabilities at all, don't show the product (must have outlet-specific availability)
      return false;
      
      // If no outlet_availabilities, check product-level availability
      return p.is_available === "Yes";
    });
    
    // Group by category name
    const categoryToProducts = {};
    filteredProducts.forEach((p) => {
      const category = p.product_category_name || 'UNCATEGORIZED';
      if (!categoryToProducts[category]) categoryToProducts[category] = [];
      
      // Find outlet-specific availability
      let priceFromOutlet = p.price != null ? Number(p.price) : 0;
      let availableQuantity = null;
      
      if (Array.isArray(p.outlet_availabilities) && p.outlet_availabilities.length > 0 && outletId) {
        // Find specific outlet availability - convert to numbers for comparison
        const outletIdNum = Number(outletId);
        const outletAvailability = p.outlet_availabilities.find(oa => Number(oa.outlet_id) === outletIdNum);
        if (outletAvailability) {
          priceFromOutlet = Number(outletAvailability.price) || priceFromOutlet;
          availableQuantity = outletAvailability.quantity != null ? Number(outletAvailability.quantity) : null;
        }
      }
      
      categoryToProducts[category].push({
        id: p.id,
        name: p.product_name || 'Unnamed',
        price: Number.isFinite(priceFromOutlet) ? priceFromOutlet : 0,
        gst: p.gst,
        availableQuantity: availableQuantity,
        fullProductData: p, // Store full product data for reference
      });
    });

    const options = [];
    Object.keys(categoryToProducts).forEach((categoryName) => {
      options.push({ value: `header-${categoryName}`, label: categoryName, isHeader: true });
      categoryToProducts[categoryName].forEach((prod) => {
        // Hide products that have explicit zero (or negative) stock.
        if (prod.availableQuantity !== null && prod.availableQuantity <= 0) {
          return;
        }

        // Calculate current cart quantity for this product
        const currentCartQty = getCurrentCartQuantity(prod.id, items);
        // Check if product is disabled (available quantity reached)
        const isDisabled = prod.availableQuantity !== null && currentCartQty >= prod.availableQuantity;

        options.push({
          value: `product-${prod.id}`,
          label: `${prod.name}`,
          right: `$${Number(prod.price).toFixed(2)}`,
          price: Number(prod.price),
          gst: prod.gst,
          name: prod.name,
          category: categoryName,
          availableQuantity: prod.availableQuantity,
          fullProductData: prod.fullProductData,
          disabled: isDisabled, // Mark as disabled if quantity limit reached
        });
      });
    });
    return options;
  };


  // Fetch outlet data when sidebar opens if not available
  const isOpenRef = React.useRef(open);
  const sessionRef = React.useRef(0);

  useEffect(() => {
    isOpenRef.current = open;
    if (open) {
      sessionRef.current += 1;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    
    const fetchOutletData = async () => {
      const outletId = selectedOutlet || loggedOutletID;
      
      // If we already have selectedOutletData from context, use it
      if (selectedOutletData) {
        setLocalOutletData(selectedOutletData);
        return;
      }
      
      // If no outlet data and we have an outlet ID, fetch it
      if (outletId && !localOutletData) {
        try {
          const response = await getSelectedOutlet(outletId, { is_image: 0 });
          const data = extractOutletFromApiResponse(response);
          if (data) {
            setLocalOutletData(data);
            logger.log("Fetched outlet data:", data);
          }
        } catch (error) {
          logger.error("Error fetching outlet data:", error);
        }
      }
    };
    
    fetchOutletData();
  }, [open, selectedOutlet, loggedOutletID, selectedOutletData, localOutletData]);

  useEffect(() => {
    // Fetch products and voucher catalogue when sidebar opens or outlet changes
    if (!open) return;
    let isMounted = true;
    (async () => {
      try {
        setIsLoadingProducts(true);
        setIsLoadingVouchers(true);
        setProductsError('');
        setVouchersError('');
        const outletId = selectedOutlet || loggedOutletID;
        const serviceList = await fetchAllOutletProducts(outletId);
        if (!Array.isArray(serviceList)) {
          throw new Error('Invalid products response');
        }

        let voucherList = [];
        try {
          const fetchedV = await fetchAllVoucherProducts();
          voucherList = Array.isArray(fetchedV) ? fetchedV : [];
        } catch (e) {
          if (isMounted) setVouchersError('Failed to load vouchers');
        }

        if (!isMounted) return;

        const opts = buildOptionsFromProductsApi(serviceList, outletId);
        setProductsData(serviceList);
        setProductOptions(opts);
        setVoucherProductsData(voucherList);
      } catch (err) {
        if (isMounted) setProductsError('Failed to load products');
      } finally {
        if (isMounted) {
          setIsLoadingProducts(false);
          setIsLoadingVouchers(false);
        }
      }
    })();
    return () => { isMounted = false; };
  }, [open, selectedOutlet, loggedOutletID]);

  // Recalculate product options when items change to update disabled states
  useEffect(() => {
    if (!open || productsData.length === 0) return;
    const outletId = selectedOutlet || loggedOutletID;
    const opts = buildOptionsFromProductsApi(productsData, outletId);
    setProductOptions(opts);
  }, [items, open, selectedOutlet, loggedOutletID, productsData]);

  useEffect(() => {
    // Compute subtotal considering per-line membership discount and push to global payment context
    setServiceTotal(productDiscountedSubtotal);
  }, [productDiscountedSubtotal, setServiceTotal]);

  useEffect(() => {
    if (!open || isCheckoutLocked) return;
    if (hasVoucherItems && isMembershipApplied) {
      setIsMembershipApplied(false);
    }
  }, [open, hasVoucherItems, isMembershipApplied, isCheckoutLocked, setIsMembershipApplied]);

  // Global membership toggle applies member rate to all catalog-priced lines
  useEffect(() => {
    if (!open || isCheckoutLocked) return;

    setItems((prev) => {
      if (prev.length === 0) return prev;

      return prev.map((item) => ({
        ...item,
        isMemberRateApplied:
          !item.isVoucher &&
          isMembershipApplied &&
          Number(item.price) === Number(item.originalPrice),
      }));
    });
  }, [isMembershipApplied, open, isCheckoutLocked]);

  // Sync toggle UI from cart lines when returning to checkout (e.g. after partial payment)
  useEffect(() => {
    if (!open || step !== 'checkout') return;

    const currentItems = itemsRef.current;
    if (currentItems.length === 0) return;

    const hasAnyMemberRate = currentItems.some((item) => item.isMemberRateApplied);
    setIsMembershipApplied(hasAnyMemberRate);
  }, [open, step, setIsMembershipApplied]);

  // Turn off membership toggle when all product member rate tags are removed
  useEffect(() => {
    if (!open || items.length === 0 || isCheckoutLocked) return;

    const membershipJustTurnedOn =
      isMembershipApplied && !prevMembershipAppliedRef.current;
    prevMembershipAppliedRef.current = isMembershipApplied;

    if (membershipJustTurnedOn) return;

    const hasAnyMemberRate = items.some((item) => !item.isVoucher && item.isMemberRateApplied);
    if (!hasAnyMemberRate && isMembershipApplied) {
      setIsMembershipApplied(false);
    }
  }, [items, open, isMembershipApplied, setIsMembershipApplied, isCheckoutLocked]);

  // Update available quantities when outlet changes
  useEffect(() => {
    if (!open || items.length === 0) return;
    
    const outletId = selectedOutlet || loggedOutletID;
    setItems((prev) => {
      return prev.map(item => {
        if (item.isVoucher) {
          return {
            ...item,
            availableQuantity: null,
            quantityError: null,
          };
        }
        const availableQty = getAvailableQuantity(item.productId, outletId);
        const quantityError = availableQty !== null && item.qty > availableQty 
          ? `Only ${availableQty} available` 
          : null;
        return {
          ...item,
          availableQuantity: availableQty,
          quantityError: quantityError
        };
      });
    });
  }, [selectedOutlet, loggedOutletID, open, productsData]);

  

  const prevOpenRef = React.useRef(false);

  const resetProductCheckoutSession = useCallback(() => {
    setItems([]);
    setStep('checkout');
    setServiceTotal(0);
    setNote('');
    setSelectedPaymentMethod(null);
    setBookingInfo(null);
    setCustomerData(null);
    setBookingDataFromAPI(null);
    setCustomerDataFromAPI(null);
    setBookingType(null);
    setIsCustomerSelectedLocal(false);
    setLocalOutletData(null);
    setPaymentHistory([]);
    setAmountDue('0.00');
    setAmountToCharge('');
    setCurrentPaymentMethod('');
    setCurrentPaymentMethodId(null);
    setProductsData([]);
    setShowPaymentInProgressNotice(false);
    setIsCustomerSelected(false);
    setCustomerApiError("");
  }, [
    setAmountDue,
    setAmountToCharge,
    setCurrentPaymentMethod,
    setCurrentPaymentMethodId,
    setIsCustomerSelected,
    setPaymentHistory,
    setServiceTotal,
  ]);

  const resetStalePaymentState = useCallback(() => {
    setPaymentHistory([]);
    setAmountDue('0.00');
    setAmountToCharge('');
    setCurrentPaymentMethod('');
    setCurrentPaymentMethodId(null);
    setServiceTotal(0);
    setBookingDataFromAPI(null);
    setStep('checkout');
    setShowPaymentInProgressNotice(false);
    setCustomerApiError("");
  }, [
    setAmountDue,
    setAmountToCharge,
    setCurrentPaymentMethod,
    setCurrentPaymentMethodId,
    setPaymentHistory,
    setServiceTotal,
  ]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !wasOpen) {
      // Clear payment state left over from a sales sidebar / prior partial payment flow.
      resetStalePaymentState();
      return;
    }

    if (!open && wasOpen) {
      resetProductCheckoutSession();
    }
  }, [open, resetProductCheckoutSession, resetStalePaymentState]);

  // Sync local customer selection state with global state
  useEffect(() => {
    if (open) {
      setIsCustomerSelectedLocal(isCustomerSelected);
    }
  }, [open, isCustomerSelected]);

  useEffect(() => {
    if (isCustomerSelected) {
      setCustomerApiError("");
    }
  }, [isCustomerSelected]);

  // Helper function to get available quantity for a product
  const getAvailableQuantity = (productId, outletId = null) => {
    const product = productsData.find(p => p.id === productId);
    if (!product) return null;
    
    if (Array.isArray(product.outlet_availabilities) && product.outlet_availabilities.length > 0) {
      if (outletId) {
        // Convert both to numbers for comparison to handle string/number mismatches
        const outletIdNum = Number(outletId);
        const outletAvailability = product.outlet_availabilities.find(oa => Number(oa.outlet_id) === outletIdNum);
        if (outletAvailability && outletAvailability.quantity != null) {
          return Number(outletAvailability.quantity);
        }
      }
      // Fallback to first availability
      if (product.outlet_availabilities[0].quantity != null) {
        return Number(product.outlet_availabilities[0].quantity);
      }
    }
    
    // Fallback to product-level quantity
    if (product.quantity != null) {
      return Number(product.quantity);
    }
    
    return null;
  };

  // Helper function to get outlet_availability_id for a product
  const getOutletAvailabilityId = (productId, outletId = null) => {
    const product = productsData.find(p => p.id === productId);
    if (!product) return null;
    
    if (Array.isArray(product.outlet_availabilities) && product.outlet_availabilities.length > 0) {
      if (outletId) {
        const outletIdNum = Number(outletId);
        const outletAvailability = product.outlet_availabilities.find(oa => Number(oa.outlet_id) === outletIdNum);
        if (outletAvailability && outletAvailability.id) {
          return outletAvailability.id;
        }
      }
      // Fallback to first availability
      if (product.outlet_availabilities[0].id) {
        return product.outlet_availabilities[0].id;
      }
    }
    
    return null;
  };

  // Helper function to get current total quantity of a product in cart
  const getCurrentCartQuantity = (productId, currentItems) => {
    return currentItems
      .filter(item => item.productId === productId)
      .reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
  };

  const getItemAvailableQuantity = (item) => {
    if (item.isVoucher) return null;
    const outletId = selectedOutlet || loggedOutletID;
    return item.availableQuantity !== null
      ? item.availableQuantity
      : getAvailableQuantity(item.productId, outletId);
  };

  const voucherOptions = useMemo(
    () =>
      buildVoucherOptionsFromList(voucherProductsData, items, (id) =>
        items
          .filter((it) => it.productId === id && it.isVoucher)
          .reduce((sum, it) => sum + (Number(it.qty) || 0), 0)
      ),
    [voucherProductsData, items]
  );

  const findOptionByDropdownValue = (value) =>
    productOptions.find((o) => o.value === value && !o.isHeader) ||
    voucherOptions.find((o) => o.value === value && !o.isHeader) ||
    null;

  const syncVoucherVerifyAfterQtyChange = useCallback((lineId, beforeCodes, afterCodes) => {
    setVoucherVerifyByKey((prev) => remapVoucherVerifyForQtyChange(prev, lineId, beforeCodes, afterCodes));
  }, []);

  const clearVoucherVerifyForItem = useCallback((lineId) => {
    if (!lineId) return;
    const prefix = `${lineId}-`;
    setVoucherVerifyByKey((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(prefix)) delete next[k];
      });
      return next;
    });
  }, []);

  const getCurrentVoucherVerifyKey = useCallback((lineId, fallbackCodeIdx, serialRaw) => {
    const fallbackKey = voucherVerifySlotKey(lineId, fallbackCodeIdx);
    const serial = String(serialRaw || '').trim();
    const item = itemsRef.current.find((row) => row.isVoucher && row.lineId === lineId);
    if (!item || !serial) return fallbackKey;

    const codes = normalizeVoucherCodes(item.voucherCodes, item.qty);
    const currentIdx = codes.findIndex((code) => String(code || '').trim() === serial);
    return currentIdx >= 0 ? voucherVerifySlotKey(lineId, currentIdx) : fallbackKey;
  }, []);

  const setVoucherVerifyForCurrentSlot = useCallback((lineId, fallbackCodeIdx, serial, state) => {
    const fallbackKey = voucherVerifySlotKey(lineId, fallbackCodeIdx);
    const currentKey = getCurrentVoucherVerifyKey(lineId, fallbackCodeIdx, serial);
    setVoucherVerifyByKey((prev) => {
      const next = { ...prev, [currentKey]: state };
      if (currentKey !== fallbackKey) delete next[fallbackKey];
      return next;
    });
  }, [getCurrentVoucherVerifyKey]);

  const verifyVoucherSerial = useCallback(
    async (lineId, codeIdx, serialRaw) => {
      const serial = String(serialRaw || '').trim().replace(/\s/g, '');
      const key = voucherVerifySlotKey(lineId, codeIdx);
      if (!serial) {
        setVoucherVerifyByKey((prev) => ({ ...prev, [key]: { status: 'idle', message: undefined } }));
        return;
      }
      if (isCartVoucherSerialPresent(
        items.map((it) => {
          if (!it.isVoucher || it.lineId !== lineId) return it;
          const codes = normalizeVoucherCodes(it.voucherCodes, it.qty);
          const filteredCodes = codes.filter((_, i) => i !== codeIdx);
          return { ...it, voucherCodes: filteredCodes, qty: filteredCodes.length || 1 };
        }),
        serial
      )) {
        showAppToast('error', 'This voucher serial is already in the cart.');
        setVoucherVerifyByKey((prev) => ({
          ...prev,
          [key]: { status: 'error', message: 'Duplicate serial in cart' },
        }));
        return;
      }
      const outletId = Number(selectedOutlet || loggedOutletID);
      if (!Number.isFinite(outletId) || outletId <= 0) {
        showAppToast('error', 'Select an outlet before verifying vouchers.');
        setVoucherVerifyByKey((prev) => ({
          ...prev,
          [key]: { status: 'error', message: 'No outlet selected' },
        }));
        return;
      }

      const service_ids = extractServiceIdsFromBooking(bookingDataFromAPI);
      const cartVoucherItem = items.find((it) => it.isVoucher && it.lineId === lineId);
      const voucherId = Number(cartVoucherItem?.productId);
      setVoucherVerifyByKey((prev) => ({
        ...prev,
        [key]: { status: 'loading', message: undefined },
      }));

      const resp = await postVoucherVerify({
        serial_number: serial,
        outlet_id: outletId,
        panel: 'outlet',
        service_ids,
        ...(Number.isFinite(voucherId) && voucherId > 0 ? { voucher_id: voucherId } : {}),
      });

      if (resp != null && typeof resp === 'object' && isVoucherVerifyResponseOk(resp)) {
        const successMessage = getVoucherVerifySuccessMessage(resp);
        showAppToast('success', successMessage);
        setVoucherVerifyForCurrentSlot(lineId, codeIdx, serial, {
          status: 'success',
          message: successMessage,
        });
        return;
      }

      const msg = getVoucherVerifyErrorMessage(resp);
      showAppToast('error', String(msg));
      setVoucherVerifyForCurrentSlot(lineId, codeIdx, serial, {
        status: 'error',
        message: String(msg),
      });
    },
    [selectedOutlet, loggedOutletID, bookingDataFromAPI, items, setVoucherVerifyForCurrentSlot]
  );

  const handleVoucherCodeChange = (lineId, codeIdx, value, itemIdxFallback) => {
    const sanitized = String(value || '').replace(/\s/g, '');
    if (lineId != null) {
      const key = voucherVerifySlotKey(lineId, codeIdx);
      setVoucherVerifyByKey((prev) => ({
        ...prev,
        [key]: { status: 'idle', message: undefined },
      }));
    }
    setItems((prev) => {
      const itemIdx =
        lineId != null ? prev.findIndex((row) => row.lineId === lineId) : itemIdxFallback;
      if (itemIdx === -1) return prev;
      const item = prev[itemIdx];
      if (!item?.isVoucher) return prev;
      const next = [...prev];
      const codes = normalizeVoucherCodes(item.voucherCodes, item.qty);
      codes[codeIdx] = sanitized;
      next[itemIdx] = { ...item, voucherCodes: codes };
      return next;
    });
  };

  const handleClearVoucherCode = (lineId, codeIdx, itemIdxFallback) => {
    if (lineId != null) {
      skipVoucherVerifyBlurRef.current[voucherVerifySlotKey(lineId, codeIdx)] = true;
      const prefix = `${lineId}-`;
      setVoucherVerifyByKey((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (k.startsWith(prefix) && k === voucherVerifySlotKey(lineId, codeIdx)) delete next[k];
        });
        return next;
      });
    }
    handleVoucherCodeChange(lineId, codeIdx, '', itemIdxFallback);
  };

  const handleVoucherInputBlur = (lineId, codeIdx, value) => {
    if (!lineId) return;
    const key = voucherVerifySlotKey(lineId, codeIdx);
    if (skipVoucherVerifyBlurRef.current[key]) {
      delete skipVoucherVerifyBlurRef.current[key];
      return;
    }
    verifyVoucherSerial(lineId, codeIdx, value);
  };

  const focusVoucherScanInput = (lineId, itemIdx, codeIdx = 0) => {
    const rk = lineId != null ? `${lineId}-${codeIdx}` : `idx${itemIdx}-${codeIdx}`;
    const input = voucherScanInputRefs.current[rk];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const focusPendingVoucherInput = useCallback(() => {
    const target = pendingVoucherFocusRef.current;
    if (!target) return;

    pendingVoucherFocusRef.current = null;
    window.setTimeout(() => {
      const input = voucherScanInputRefs.current[target.refKey];
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }, []);

  const addScannedVerifiedVoucherToCart = useCallback(
    (serial, catalogProduct, successMessage) => {
      const normalizedSerial = String(serial || '').trim();
      if (!normalizedSerial || !catalogProduct) return;
      if (itemsRef.current.some((item) => !item.isVoucher)) {
        showAppToast('error', 'Complete or clear product items before adding vouchers.');
        return;
      }

      const opt = buildOptionFromVoucherCatalogProduct(catalogProduct);
      const verifyPatch = {};

      setItems((prev) => {
        if (isCartVoucherSerialPresent(prev, normalizedSerial)) {
          return prev;
        }

        const productId = catalogProduct.id;
        const existingIndex = prev.findIndex(
          (it) => it.isVoucher && Number(it.productId) === Number(productId)
        );

        if (existingIndex !== -1) {
          const next = [...prev];
          const existing = next[existingIndex];
          const codes = normalizeVoucherCodes(existing.voucherCodes, existing.qty);
          const emptyIdx = codes.findIndex((c) => !String(c || '').trim());

          if (emptyIdx >= 0) {
            codes[emptyIdx] = normalizedSerial;
            verifyPatch[voucherVerifySlotKey(existing.lineId, emptyIdx)] = {
              status: 'success',
              message: successMessage,
            };
            next[existingIndex] = { ...existing, voucherCodes: codes };
            return next;
          }

          const newQty = existing.qty + 1;
          const newCodes = normalizeVoucherCodes([...codes, normalizedSerial], newQty);
          verifyPatch[voucherVerifySlotKey(existing.lineId, newQty - 1)] = {
            status: 'success',
            message: successMessage,
          };
          next[existingIndex] = {
            ...existing,
            qty: newQty,
            quantityError: null,
            voucherCodes: newCodes,
          };
          return next;
        }

        const newItem = {
          ...buildItemFromOption(opt, 1),
          voucherCodes: [normalizedSerial],
        };
        verifyPatch[voucherVerifySlotKey(newItem.lineId, 0)] = {
          status: 'success',
          message: successMessage,
        };
        return [...prev, newItem];
      });

      setVoucherVerifyByKey((prev) => ({ ...prev, ...verifyPatch }));
    },
    [selectedOutlet, loggedOutletID, isMembershipApplied]
  );

  const processGlobalVoucherScan = useCallback(
    async (serialRaw) => {
      const serial = String(serialRaw || '').trim().replace(/\s/g, '');
      if (!serial || globalVoucherScanProcessingRef.current) return;

      if (itemsRef.current.some((item) => !item.isVoucher)) {
        showAppToast('error', 'Complete or clear product items before scanning vouchers.');
        return;
      }

      if (isCartVoucherSerialPresent(itemsRef.current, serial)) {
        showAppToast('info', 'This voucher is already in the cart.');
        return;
      }

      if (isLoadingVouchers) {
        showAppToast('error', 'Vouchers not loaded yet. Please wait.');
        return;
      }
      if (vouchersError) {
        showAppToast('error', 'Could not load vouchers. Please try again.');
        return;
      }

      const outletId = Number(selectedOutlet || loggedOutletID);
      if (!Number.isFinite(outletId) || outletId <= 0) {
        showAppToast('error', 'Select an outlet before verifying vouchers.');
        return;
      }

      globalVoucherScanProcessingRef.current = true;
      try {
        const resp = await postVoucherVerify({
          serial_number: serial,
          outlet_id: outletId,
          panel: 'outlet',
          service_ids: extractServiceIdsFromBooking(bookingDataFromAPI),
        });

        if (resp == null || typeof resp !== 'object' || !isVoucherVerifyResponseOk(resp)) {
          showAppToast('error', String(getVoucherVerifyErrorMessage(resp)));
          return;
        }

        const voucherPayload = getVoucherFromVerifyResponse(resp);
        if (voucherPayload && voucherPayload.can_sell === false) {
          showAppToast('error', 'This voucher cannot be sold.');
          return;
        }

        const catalogProduct = findVoucherCatalogProduct(
          voucherProductsDataRef.current,
          voucherPayload
        );
        if (!catalogProduct) {
          showAppToast('error', 'Scanned voucher not available!');
          return;
        }

        if (isCartVoucherSerialPresent(itemsRef.current, serial)) {
          showAppToast('info', 'This voucher is already in the cart.');
          return;
        }

        const successMessage = getVoucherVerifySuccessMessage(resp);
        addScannedVerifiedVoucherToCart(serial, catalogProduct, successMessage);
        showAppToast('success', successMessage);
      } finally {
        globalVoucherScanProcessingRef.current = false;
      }
    },
    [
      isLoadingVouchers,
      vouchersError,
      selectedOutlet,
      loggedOutletID,
      bookingDataFromAPI,
      addScannedVerifiedVoucherToCart,
    ]
  );

  useEffect(() => {
    if (!open || step !== 'checkout') {
      globalVoucherScanBufferRef.current = '';
      return undefined;
    }

    const flushBuffer = () => {
      const raw = globalVoucherScanBufferRef.current.trim();
      globalVoucherScanBufferRef.current = '';
      if (!raw) return false;
      processGlobalVoucherScan(raw);
      return true;
    };

    const onKeyDown = (e) => {
      if (isEditableInputFocused()) return;

      if (e.key === 'Enter') {
        const handled = flushBuffer();
        if (handled) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      if (e.key === 'Backspace') {
        if (globalVoucherScanBufferRef.current.length > 0) {
          globalVoucherScanBufferRef.current = globalVoucherScanBufferRef.current.slice(0, -1);
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        globalVoucherScanBufferRef.current += e.key;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      globalVoucherScanBufferRef.current = '';
    };
  }, [open, step, processGlobalVoucherScan]);

  const handleUpdateItemProduct = (idx, value) => {
    if (isCheckoutLocked) return;
    const opt = findOptionByDropdownValue(value);
    if (!opt || opt.isHeader || opt.disabled) return;

    const isVoucherLine = typeof opt.value === 'string' && opt.value.startsWith('voucher-');
    const isProductLine = typeof opt.value === 'string' && opt.value.startsWith('product-');
    const lineBefore = items[idx];

    if (lineBefore && Boolean(lineBefore.isVoucher) !== isVoucherLine) {
      showAppToast('error', 'Products and vouchers cannot be mixed in one sale.');
      return;
    }

    if (lineBefore?.lineId) clearVoucherVerifyForItem(lineBefore.lineId);

    const numericProductId = isVoucherLine
      ? Number(String(opt.value).replace('voucher-', ''))
      : isProductLine
        ? Number(String(opt.value).replace('product-', ''))
        : null;

    if (!numericProductId || Number.isNaN(numericProductId)) {
      showAppToast('error', 'Invalid selection');
      return;
    }

    const outletId = selectedOutlet || loggedOutletID;
    const availableQuantity = isVoucherLine ? null : getAvailableQuantity(numericProductId, outletId);
    if (isVoucherLine && lineBefore?.lineId) {
      pendingVoucherFocusRef.current = { refKey: `${lineBefore.lineId}-0` };
    }

    setItems((prev) => {
      const currentItem = prev[idx];
      if (!currentItem) return prev;

      const otherQty = prev
        .filter(
          (item, itemIdx) =>
            itemIdx !== idx &&
            item.productId === numericProductId &&
            Boolean(item.isVoucher) === isVoucherLine
        )
        .reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
      const totalQty = otherQty + (Number(currentItem.qty) || 0);

      if (!isVoucherLine && availableQuantity !== null && totalQty > availableQuantity) {
        showAppToast('error', `Cannot select this product. Only ${availableQuantity} available.`);
        return prev;
      }

      const next = [...prev];
      const isVoucher = isVoucherLine;
      next[idx] = {
        ...currentItem,
        name: opt.name,
        price: opt.price,
        originalPrice: opt.price,
        gst: opt.gst,
        productId: numericProductId,
        outletAvailabilityId: isVoucher ? null : getOutletAvailabilityId(numericProductId, outletId),
        availableQuantity,
        quantityError: null,
        category: opt.category || '',
        isVoucher,
        editing: false,
        isMemberRateApplied:
          !isVoucher && isMembershipApplied,
        voucherCodes: isVoucher
          ? createEmptyVoucherCodes(currentItem.qty)
          : [],
      };
      return next;
    });
    if (isVoucherLine) {
      focusPendingVoucherInput();
    }
  };

  const buildItemFromOption = (opt, qty = 1) => {
    const useVoucherPrefix = typeof opt?.value === 'string' && opt.value.startsWith('voucher-');
    const rawId = useVoucherPrefix
      ? opt.value.replace('voucher-', '')
      : opt.value?.startsWith('product-')
        ? opt.value.replace('product-', '')
        : null;
    const numericProductId = rawId ? Number(rawId) : null;
    const outletId = selectedOutlet || loggedOutletID;
    const isVoucher = useVoucherPrefix || Boolean(opt.isVoucher);
    const availableQuantity = isVoucher
      ? null
      : numericProductId
        ? getAvailableQuantity(numericProductId, outletId)
        : null;
    const outletAvailabilityId = isVoucher
      ? null
      : numericProductId
        ? getOutletAvailabilityId(numericProductId, outletId)
        : null;

    return {
      lineId: createCartLineId(),
      name: opt.name,
      price: opt.price,
      originalPrice: opt.price,
      gst: opt.gst,
      qty,
      editing: false,
      reason: '',
      productId: numericProductId,
      outletAvailabilityId,
      availableQuantity,
      quantityError: null,
      category: opt.category || '',
      isVoucher,
      isMemberRateApplied: !isVoucher && isMembershipApplied,
      voucherCodes: isVoucher ? createEmptyVoucherCodes(qty) : [],
    };
  };

  const updateItemQuantity = (idx, rawValue, { onBlur = false } = {}) => {
    if (isCheckoutLocked) return;
    setItems((prev) => {
      const next = [...prev];
      const item = next[idx];

      if (item.isVoucher) {
        if (rawValue === '') {
          next[idx] = { ...item, qty: '', quantityError: null };
          return next;
        }
        const parsedQty = parseInt(String(rawValue), 10);
        if (isNaN(parsedQty) || parsedQty < 1) {
          next[idx] = { ...item, qty: 1, quantityError: null, voucherCodes: normalizeVoucherCodes(item.voucherCodes, 1) };
          return next;
        }
        const beforeCodes = normalizeVoucherCodes(item.voucherCodes, item.qty);
        const afterCodes = normalizeVoucherCodes(item.voucherCodes, parsedQty);
        next[idx] = {
          ...item,
          qty: parsedQty,
          quantityError: null,
          voucherCodes: afterCodes,
        };
        if (item.lineId) {
          setTimeout(() => syncVoucherVerifyAfterQtyChange(item.lineId, beforeCodes, afterCodes), 0);
        }
        return next;
      }

      const availableQty = getItemAvailableQuantity(item);

      if (rawValue === '') {
        next[idx] = { ...item, qty: '', quantityError: null };
        return next;
      }

      const parsedQty = parseInt(String(rawValue), 10);
      if (isNaN(parsedQty)) {
        return prev;
      }

      if (parsedQty < 1) {
        next[idx] = { ...item, qty: 1, quantityError: null };
        return next;
      }

      if (availableQty !== null && parsedQty > availableQty) {
        if (onBlur || parsedQty !== item.qty) {
          showAppToast('error', `Cannot set quantity. Only ${availableQty} available for ${item.name}`);
        }
        next[idx] = {
          ...item,
          qty: availableQty,
          quantityError: `Maximum quantity reached. Only ${availableQty} available for this product.`,
        };
        return next;
      }

      next[idx] = { ...item, qty: parsedQty, quantityError: null };
      return next;
    });
  };

  const handleQuantityBlur = (idx) => {
    const item = items[idx];
    if (!item || isCheckoutLocked) return;
    const value = item.qty === '' || Number(item.qty) < 1 ? 1 : item.qty;
    updateItemQuantity(idx, value, { onBlur: true });
  };

  const incrementItemQuantity = (idx) => {
    if (isCheckoutLocked) return;
    const item = items[idx];
    if (!item) return;

    if (item.isVoucher) {
      const beforeCodes = normalizeVoucherCodes(item.voucherCodes, item.qty);
      const newQty = (Number(item.qty) || 0) + 1;
      const afterCodes = normalizeVoucherCodes(item.voucherCodes, newQty);
      const focusIdx = afterCodes.findIndex((code) => !String(code || '').trim());
      if (item.lineId) {
        pendingVoucherFocusRef.current = {
          refKey: `${item.lineId}-${focusIdx >= 0 ? focusIdx : newQty - 1}`,
        };
      }
      setItems((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          qty: newQty,
          quantityError: null,
          voucherCodes: afterCodes,
        };
        return next;
      });
      if (item.lineId) {
        syncVoucherVerifyAfterQtyChange(item.lineId, beforeCodes, afterCodes);
      }
      focusPendingVoucherInput();
      return;
    }

    const availableQty = getItemAvailableQuantity(item);
    const currentQty = Number(item.qty) || 0;
    const newQty = currentQty + 1;

    if (availableQty !== null && newQty > availableQty) {
      showAppToast('error', `Cannot increase quantity. Only ${availableQty} available for ${item.name}`);
      setItems((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantityError: `Maximum quantity reached. Only ${availableQty} available for this product.`,
        };
        return next;
      });
      return;
    }

    updateItemQuantity(idx, newQty);
  };

  const decrementItemQuantity = (idx) => {
    if (isCheckoutLocked) return;
    const item = items[idx];
    if (!item) return;
    const currentQty = Number(item.qty) || 1;
    const newQty = Math.max(1, currentQty - 1);

    if (item.isVoucher) {
      const beforeCodes = normalizeVoucherCodes(item.voucherCodes, item.qty);
      const afterCodes = normalizeVoucherCodes(item.voucherCodes, newQty);
      setItems((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          qty: newQty,
          quantityError: null,
          voucherCodes: afterCodes,
        };
        return next;
      });
      if (item.lineId) {
        syncVoucherVerifyAfterQtyChange(item.lineId, beforeCodes, afterCodes);
      }
      return;
    }

    updateItemQuantity(idx, newQty);
  };

  const handleAddProduct = (value) => {
    if (isCheckoutLocked) return;
    const opt = findOptionByDropdownValue(value);
    if (!opt || opt.isHeader || opt.disabled) return;

    const isVoucherLine = typeof opt.value === 'string' && opt.value.startsWith('voucher-');
    const isProductLine = typeof opt.value === 'string' && opt.value.startsWith('product-');

    if (isVoucherLine && hasProductItems) {
      showAppToast('error', 'Complete or clear product items before adding vouchers.');
      return;
    }

    if (isProductLine && hasVoucherItems) {
      showAppToast('error', 'Complete or clear voucher items before adding products.');
      return;
    }

    if (isVoucherLine) {
      if (isLoadingVouchers) {
        showAppToast('error', 'Vouchers not loaded yet. Please wait.');
        return;
      }
      if (vouchersError) {
        showAppToast('error', 'Could not load vouchers. Please try again.');
        return;
      }
      const newVoucherItem = buildItemFromOption(opt, 1);
      pendingVoucherFocusRef.current = { refKey: `${newVoucherItem.lineId}-0` };
      setItems((prev) => [...prev, newVoucherItem]);
      focusPendingVoucherInput();
      return;
    }

    if (!isProductLine) return;

    if (productOptions.length === 0) {
      showAppToast('error','Products not loaded yet. Please wait.');
      return;
    }
    
    // Extract product ID from the value (format: "product-123")
    const productId = opt.value.startsWith('product-') ? opt.value.replace('product-', '') : null;
    const numericProductId = productId ? Number(productId) : null;
    
    if (!numericProductId) {
      showAppToast('error','Invalid product selected');
      return;
    }
    
    const outletId = selectedOutlet || loggedOutletID;
    const availableQuantity = getAvailableQuantity(numericProductId, outletId);
    const outletAvailabilityId = getOutletAvailabilityId(numericProductId, outletId);
    
    const applyMemberRate = isMembershipApplied;

    setItems((prev) => {
      const currentCartQty = getCurrentCartQuantity(numericProductId, prev);
      const newQty = currentCartQty + 1;
      
      // Check if adding would exceed available quantity (safety check)
      if (availableQuantity !== null && newQty > availableQuantity) {
        return prev; // Silently return without adding
      }
      
      const existingIndex = prev.findIndex((it) =>
        canMergeCartLines(it, numericProductId, opt.price, applyMemberRate)
      );
      if (existingIndex !== -1) {
        const next = [...prev];
        next[existingIndex] = { 
          ...next[existingIndex], 
          qty: next[existingIndex].qty + 1,
          quantityError: null // Clear any previous error
        };
        return next;
      }
      return [...prev, { 
        lineId: createCartLineId(),
        name: opt.name, 
        price: opt.price, 
        originalPrice: opt.price, 
        gst: opt.gst,
        qty: 1, 
        editing: false, 
        reason: '',
        productId: numericProductId,
        outletAvailabilityId: outletAvailabilityId,
        availableQuantity: availableQuantity,
        quantityError: null,
        category: opt.category || '',
        isVoucher: false,
        isMemberRateApplied: applyMemberRate,
        voucherCodes: [],
      }];
    });
  };

  // Helper function to scroll content to bottom
  const scrollToBottom = () => {
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
      }
    }, 150);
  };

  // Handle back button click - navigate back in payment flow or close sidebar
  const handleBack = () => {
    if (step === 'payment') {
      // Go back to checkout (product selection) step
      setStep('checkout');
    } else if (step === 'confirmation') {
      // Go back to payment step
      setStep('payment');
    } else {
      // On checkout step, close the sidebar
      onClose();
    }
  };

  const handleOverlayClick = () => {
    if (isCheckoutTopupOpen) return;
    onClose();
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.sidebar} onClick={(e) => e.stopPropagation()}>
        {step !== 'payment' && step !== 'confirmation' && <div className={styles.header}>
          <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={handleBack}>
            <img src={BackIcon} alt="Back" />
          </button>
          <h2 className={styles.title}>
            {step === 'payment' ? 'Payment' : 
             step === 'confirmation' ? 'Payment Confirmation' : 
             'Checkout'}
          </h2>
          </div>
        </div>}

        {step === 'payment' ? (
          <div className={styles.content}>
            <PaymentPage
              onBack={() => setStep('checkout')}
              onNext={(paymentMethod) => {
                // Payment method selected, go to confirmation step
           
                setSelectedPaymentMethod(paymentMethod);
                setStep('confirmation');
              }}
              completeBooking={() => {}}
              setCurrentStep={() => {}}
              bookingDataFromAPI={bookingDataFromAPI || { deposit: 0, booking_id: null }}
              customerDataFromAPI={customerDataFromAPI || {}}
              hideHeader={true}
              isProductSale={true}
              productGstAmount={productGstAmount}
              onTopUpClick={openCheckoutMembershipFlow}
              onOpenMembershipSale={openMembershipSaleRecovery}
              customerCreditRefreshKey={checkoutTopupRefreshKey}
              paymentMethodsRefreshKey={checkoutTopupRefreshKey}
              creditFetchUserId={globalCustomerData?.id || bookingDataFromAPI?.user_id || null}
            />
          </div>
        ) : step === 'confirmation' ? (
          <div className={styles.content}>
            <ConfirmationPage
              summary={{
                subtotal: productDiscountedSubtotal,
                gst: productGstAmount,
                tip: 0,
                total: productGrandTotal
              }}
              membershipListUserId={globalCustomerData?.id || bookingDataFromAPI?.user_id || null}
              selectedOthersMembershipClient={othersMembershipClient}
              onOthersMembershipClientChange={setOthersMembershipClient}
              customerCreditRefreshKey={checkoutTopupRefreshKey}
              creditFetchUserId={
                othersMembershipClient?.user_id ??
                globalCustomerData?.id ??
                bookingDataFromAPI?.user_id ??
                null
              }
              onMembershipClientTopUpClick={openCheckoutMembershipFlow}
              onBack={() => setStep('payment')}
              onConfirm={async (paymentData) => {
                 const mySession = sessionRef.current;
                 try {
                   // Create product payment payload
                   const totalAmount = productGrandTotal;
                   
                   // Get outlet ID from selected outlet or logged-in user's outlet
                   const outletId = selectedOutlet || loggedOutletID;
                   
                   // Get outlet data - use localOutletData (fetched) or selectedOutletData (from context)
                   const outletData = localOutletData || selectedOutletData;
                   const outletType = outletData?.outlet_type_id;
                   
                   // Check if this payment will complete the amount due (not partial)
                   let currentAmountDue = parseFloat(amountDue);
                   if (paymentData?.method?.toUpperCase() === "CASH" && !isNaN(currentAmountDue)) {
                    currentAmountDue = parseFloat(roundCashAmount(currentAmountDue));
                  }
                   const paymentAmount = parseFloat(paymentData?.amount || totalAmount);
                   const remainingAfterPayment = currentAmountDue - paymentAmount;
                   const isPaymentComplete = remainingAfterPayment <= 0;
                   
                   // Get user_id from localStorage
                  const stringId = localStorage.getItem('user_id');
                  const userId = stringId ? Number(stringId) : null;
                  const method =
                    paymentData?.method || selectedPaymentMethod || 'payatstore';

          
                   
                   const payload = {
                        outlet: outletId,
                        outlet_type: outletType,
                        customer: globalCustomerData?.id || '',
                        items: buildProductPaymentApiItems(
                          items,
                          globalCustomerData?.name || '',
                          voucherVerifyByKey
                        ),
                    sub_total: productDiscountedSubtotal,
                    gst_amount: Number(productGstAmount.toFixed(2)),
                    grand_total: totalAmount,
                    currency: "SGD",
                    amount: paymentData?.amount || totalAmount, // Amount Received value
                    method: method,
                    payment_type: method || 'payatstore',
                    reference: paymentData?.reference || "",
                    payment_method_id: paymentData?.payment_method_id,
                    payment_status: isPaymentComplete ? 2 : 3, // 2 = Full payment, 3 = Partial payment // 1 if payment completes, 0 if partial
                    service_at: new Date().toLocaleDateString('en-GB') + " " + new Date().toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}),
                    note,
                    newsletter: 0,
                    panel: "outlet",
                    type: hasVoucherItems ? "voucher" : "product",
                    created_by: userId,
                    updated_by: loggedUser ?? userId,
                    vouchers: voucherDetail?.length ? JSON.stringify(voucherDetail) : null,
                    ...(method === 'cashless' ||
                    method === 'other_membership'
                      ? {
                          skip_password: paymentData?.skip_password,
                          skip_password_reason: paymentData?.skip_password_reason,
                        }
                      : {}),
                    ...(paymentData?.selectedUserId
                      ? { membership_user_id: paymentData.selectedUserId }
                      : {}),
                    ...buildMembershipIdPaymentFields(paymentData?.membershipId),
                  };
                  
                  // Add booking_id for partial payments (if it exists)
                  if (bookingDataFromAPI?.booking_id) {
                    payload.booking_id = bookingDataFromAPI.booking_id;
                  }
                  
            
                  
                  // Call the API
                  logger.log("payload", payload);
                  logger.log("payload from checkout sidebar--->", payload);
                  const response = await postProductPayment(payload);
                
                  
                  // Check success across both API shapes
                  const isPaymentSuccess = Boolean(response?.success || response?.data?.success);
                  if (isPaymentSuccess) {
                    setCheckoutTopupRefreshKey((previousValue) => previousValue + 1);
                    const paymentStatus = response?.data?.data?.status || response?.data?.status;
                    const checkoutSaleType = hasVoucherItems ? 'voucher' : 'product';
                    const saleIdFromResponse = extractSaleIdFromPaymentResponse(
                      response,
                      bookingDataFromAPI?.booking_id
                    );
                    let currentBookingId = saleIdFromResponse || bookingDataFromAPI?.booking_id;

                    if (saleIdFromResponse) {
                      setBookingDataFromAPI((prev) => ({
                        ...(prev || {}),
                        booking_id: saleIdFromResponse,
                        deposit: prev?.deposit ?? 0,
                      }));
                      setBookingType(checkoutSaleType);
                    }
                    
                    if (!currentBookingId) {
                      try {
                        const latestSale = await fetchLatestProductSale(globalCustomerData?.id, checkoutSaleType);
                        if (latestSale?.id) {
                          currentBookingId = latestSale.id.toString().replace('#', '');
                          setBookingDataFromAPI({
                            booking_id: currentBookingId,
                            deposit: 0
                          });
                          setBookingType(checkoutSaleType);
                        }
                      } catch (error) {
                        logger.error('Error fetching booking ID:', error);
                      }
                    }
                    
                    // Add payment to history (like RightSidebarWrapper)
                    const newHistory = [
                      ...paymentHistory,
                      {
                        method: paymentData?.method,
                        amount: paymentData?.amount,
                        id: currentBookingId || null
                      }
                    ];
                    setPaymentHistory(newHistory);


                    // Calculate remaining amount due (like RightSidebarWrapper)
                    let updatedDue = currentAmountDue - paymentData?.amount;
                    
                    // Apply cash rounding to the updated amount due if payment method is CASH
                    if (paymentData?.method?.toLowerCase() === "cash" && !isNaN(updatedDue)) {
                      updatedDue = parseFloat(roundCashAmount(updatedDue));
                    }
                    
                    // Calculate remaining amount due (like RightSidebarWrapper)
                    const newDue = updatedDue < 0 ? 0 : updatedDue;
                    setAmountDue(newDue.toFixed(2));

                    
                    const normalizedPaymentStatus = String(paymentStatus || '').trim().toLowerCase();
                    const isFullPayment =
                      normalizedPaymentStatus === "completed" ||
                      normalizedPaymentStatus === "paid";
                    
                    if (newDue > 0) {
                      // Trigger sales list refresh in Bookings page to show updated payment
                      window.dispatchEvent(new CustomEvent('salesPaymentSuccess', {
                        detail: { bookingId: currentBookingId },
                      }));
                      
                      setStep('payment');
                    } else {
                      // Full payment completed — hand off to the sales sidebar
                      showAppToast('success', 'Product payment completed successfully!');
                      
                      // Trigger sales list refresh in Bookings page
                      window.dispatchEvent(new CustomEvent('salesPaymentSuccess', {
                        detail: { bookingId: currentBookingId, saleId: currentBookingId },
                      }));
                      
                      // Call outlet availability check API if full payment is successful
                      if ((isFullPayment || newDue <= 0) && items.length > 0) {
                        try {
                          const availabilityPayload = {
                            products: items
                              .filter(item => item.outletAvailabilityId)
                              .map(item => ({
                                outlet_availability_id: item.outletAvailabilityId,
                                quantity: item.qty
                              }))
                          };
                          
                          if (availabilityPayload.products.length > 0) {
                            await checkOutletAvailability(availabilityPayload);
                          }
                        } catch (error) {
                          logger.error('Error checking outlet availability:', error);
                        }
                      }
                      
                      try {
                        if (!isOpenRef.current || sessionRef.current !== mySession) return;

                        let completedSale = null;
                        const saleIdFromResponse = extractSaleIdFromPaymentResponse(response, currentBookingId);

                        if (saleIdFromResponse) {
                          completedSale = {
                            id: saleIdFromResponse,
                            type: hasVoucherItems ? 'voucher' : 'product',
                            status: paymentStatus || 'paid',
                            payment_status: paymentStatus || 'paid',
                          };
                        } else {
                          const latestSale = await fetchLatestProductSale(
                            globalCustomerData?.id,
                            hasVoucherItems ? 'voucher' : 'product'
                          );
                          if (latestSale?.id) {
                            const saleId = latestSale.id.toString().replace('#', '');
                            completedSale = {
                              ...latestSale,
                              id: saleId,
                              type: hasVoucherItems ? 'voucher' : 'product',
                              status: latestSale.status || paymentStatus || 'paid',
                              payment_status: latestSale.payment_status || paymentStatus || 'paid',
                            };
                          }
                        }

                        if (!isOpenRef.current || sessionRef.current !== mySession) return;

                        if (completedSale && typeof onSaleCompleted === 'function') {
                          onSaleCompleted(completedSale);
                        } else if (!completedSale) {
                          showAppToast('error', 'Unable to open completed sale.');
                        }
                      } catch (error) {
                        logger.error('Error opening completed sale:', error);
                        showAppToast('error', 'Unable to open completed sale.');
                      }
                    }
                  } else {
                    const referenceErrors = response?.errors?.reference || response?.data?.errors?.reference;
                    if ((Array.isArray(referenceErrors) && referenceErrors.length > 0) || (typeof referenceErrors === 'string' && referenceErrors.trim())) {
                      return response;
                    }
                    showAppToast('error',response?.message || response?.data?.message || "Payment failed. Please try again.");
                    return response;
                  }
                } catch (error) {
                  logger.error('Payment error:', error);
                  const referenceErrors = error?.response?.data?.errors?.reference;
                  if ((Array.isArray(referenceErrors) && referenceErrors.length > 0) || (typeof referenceErrors === 'string' && referenceErrors.trim())) {
                    return error?.response?.data || error?.response;
                  }
                  showAppToast('error',"Payment failed. Please try again.");
                  return error?.response?.data || { success: false, message: error?.message || "Payment failed. Please try again." };
                }
              }}
              onCancel={() => setStep('payment')}
              bookingID={bookingDataFromAPI?.booking_id || null}
              hideHeader={true}
              isProductSale={true}
            />
          </div>
        ) : (
        <div className={styles.content} ref={contentRef}>
          <div className={styles.inputGroup}>
            <CustomerSelector
              error=""
              setError={() => {}}
              setApiErrorForCreateBooking={setCustomerApiError}
              hideTagging={true}
              showMembershipToggle={true}
              preventChangesOverride={isCheckoutLocked}
              onLockedEditClick={showPaymentInProgress}
              membershipToggleBlocked={hasVoucherItems}
              onMembershipToggleBlocked={() => {
                showAppToast('info', 'Member Rate does not apply to Vouchers.');
              }}
            />
          </div>

          {items.length === 0 && isCustomerSelectedLocal && (
            <div className={`${styles.addProductRow} ${styles.addProductRowInitial}`}>
              <div className={styles.addRowAction}>
                <QuantityCircleAddIcon className={styles.addRowIcon} size={18} color="#767676" aria-hidden />
                <div className={styles.addRowDropdownWrap}>
                  <CustomDropdown
                    options={productOptions}
                    value={null}
                    onChange={handleAddProduct}
                    placeholder="Add product"
                    disabled={isLoadingProducts || !!productsError || isCheckoutLocked}
                    disableSearch={false}
                    autoFocusSearch
                    searchPlaceholder="Search ..."
                    renderRight={(opt) => opt.right}
                    triggerPaddingX={4}
                    hideUnderline
                    hideIcon
                    compactTrigger
                    triggerColor="var(--neutral-500)"
                    onOpen={scrollToBottom}
                    alignMenuToRow
                    menuClassName="checkoutAddRowDropdownMenu"
                  />
                </div>
              </div>
              {!hideCheckoutVoucherAddOnStaging && (
              <div className={styles.addRowAction}>
                <QuantityCircleAddIcon className={styles.addRowIcon} size={18} color="#767676" aria-hidden />
                <div className={styles.addRowDropdownWrap}>
                  {voucherOptions.some((o) => !o.isHeader) ? (
                    <CustomDropdown
                      options={voucherOptions}
                      value={null}
                      onChange={handleAddProduct}
                      placeholder="Add voucher"
                      disabled={isLoadingVouchers || !!vouchersError || isCheckoutLocked}
                      disableSearch={false}
                      autoFocusSearch
                      searchPlaceholder="Search ..."
                      renderRight={(opt) => opt.right}
                      triggerPaddingX={4}
                      hideUnderline
                      hideIcon
                      compactTrigger
                      triggerColor="var(--neutral-500)"
                      onOpen={scrollToBottom}
                      alignMenuToRow
                      menuClassName="checkoutAddRowDropdownMenu"
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.addRowStaticMuted}
                      disabled={isLoadingVouchers || !!vouchersError}
                      onClick={() => {
                        if (!isLoadingVouchers && !vouchersError) {
                          showAppToast('info', 'No vouchers available.');
                        }
                      }}
                    >
                      Add voucher
                    </button>
                  )}
                </div>
              </div>
              )}
            </div>
          )}

          {items.length === 0 && !isCustomerSelectedLocal && (
            <div className={`${styles.addProductRow} ${styles.addProductRowInitial}`}>
              <div className={styles.addRowAction}>
                <div className={styles.addRowDropdownWrap}>
                  <CustomDropdown
                    options={productOptions}
                    value={null}
                    onChange={handleAddProduct}
                    placeholder="Select a product"
                    disabled={true}
                    disableSearch={false}
                    searchPlaceholder="Search ..."
                    renderRight={(opt) => opt.right}
                    triggerPaddingX={4}
                    hideUnderline
                    hideIcon
                    compactTrigger
                    triggerColor="var(--neutral-500)"
                  />
                </div>
              </div>
              {!hideCheckoutVoucherAddOnStaging && (
              <div className={styles.addRowAction}>
                <div className={styles.addRowDropdownWrap}>
                  <CustomDropdown
                    options={voucherOptions}
                    value={null}
                    onChange={handleAddProduct}
                    placeholder="Add voucher"
                    disabled={true}
                    disableSearch={false}
                    searchPlaceholder="Search ..."
                    renderRight={(opt) => opt.right}
                    triggerPaddingX={4}
                    hideUnderline
                    hideIcon
                    compactTrigger
                    triggerColor="var(--neutral-500)"
                  />
                </div>
              </div>
              )}
            </div>
          )}

          {/* Selected items list */}
          {items.map((it, idx) => {
            const voucherCodes = it.isVoucher ? normalizeVoucherCodes(it.voucherCodes, it.qty) : [];
            const lineDisplayTotal = getItemDisplayLineAmount(it);
            const lineTotal = getItemLineAmount(it, voucherVerifyByKey);
            const catalogLineTotal = Number(it.originalPrice || it.price || 0) * (Number(it.qty) || 0);
            const showMemberRateStrikethrough =
              !it.isVoucher &&
              it.isMemberRateApplied &&
              catalogLineTotal > lineTotal;
            const lineAvailableQty = getItemAvailableQuantity(it);

            return (
            <div
              key={it.lineId || `${it.productId}-${idx}`}
              className={styles.lineItem}
              {...lockedEditClickProps}
            >
              <div className={styles.lineItemHeader}>
                <div className={styles.itemNameDropdown}>
                  <CustomDropdown
                    options={it.isVoucher ? voucherOptions : productOptions}
                    value={it.isVoucher ? `voucher-${it.productId}` : `product-${it.productId}`}
                    onChange={(value) => handleUpdateItemProduct(idx, value)}
                    placeholder={it.name}
                    disabled={
                      isCheckoutLocked ||
                      isLoadingProducts ||
                      !!productsError ||
                      (it.isVoucher && (isLoadingVouchers || !!vouchersError))
                    }
                    disableSearch={false}
                    autoFocusSearch
                    searchPlaceholder="Search ..."
                    renderRight={(opt) => opt.right}
                    triggerPaddingX={0}
                    hideUnderline
                    compactTrigger
                    triggerColor="var(--neutral-900)"
                  />
                </div>
                {!isCheckoutLocked && (
                <button
                  className={styles.itemDelete}
                  onClick={() => {
                    const removed = items[idx];
                    if (removed?.lineId) clearVoucherVerifyForItem(removed.lineId);
                    setItems(items.filter((_, i) => i !== idx));
                  }}
                  title="Remove"
                >
                  <img src={Deleteicon} alt="delete" />
                </button>
                )}
              </div>
              <div className={styles.lineItemRow}>
                <span className={styles.muted}>Quantity:</span>
                <div className={styles.qtyStepper}>
                  <button
                    type="button"
                    className={styles.qtyBtn}
                    onClick={() => decrementItemQuantity(idx)}
                    disabled={isCheckoutLocked || Number(it.qty) <= 1}
                    aria-label="Decrease quantity"
                  >
                    <QuantityCircleRemoveIcon />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={styles.qtyInput}
                    value={it.qty}
                    disabled={isCheckoutLocked}
                    readOnly={isCheckoutLocked}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d]/g, '');
                      updateItemQuantity(idx, value);
                    }}
                    onBlur={() => handleQuantityBlur(idx)}
                    aria-label="Quantity"
                  />
                  <button
                    type="button"
                    className={styles.qtyBtn}
                    onClick={() => incrementItemQuantity(idx)}
                    disabled={
                      isCheckoutLocked ||
                      (lineAvailableQty != null && Number(it.qty) >= lineAvailableQty)
                    }
                    aria-label="Increase quantity"
                  >
                    <QuantityCircleAddIcon />
                  </button>
                </div>
                <div className={styles.priceBlock}>
                  <span className={styles.unitPriceLabel}>Price:</span>
                  {showMemberRateStrikethrough ? (
                    <span className={styles.priceValueGroup}>
                      <span className={styles.strikePrice}>${catalogLineTotal.toFixed(2)}</span>
                      <span className={styles.unitPrice}>${lineDisplayTotal.toFixed(2)}</span>
                    </span>
                  ) : (
                    <span className={styles.unitPrice}>${lineDisplayTotal.toFixed(2)}</span>
                  )}
                  {!it.isVoucher && !isCheckoutLocked && (
                    <button
                      className={styles.editBtn}
                      title="Edit price"
                      onClick={() => setItems((prev) => {
                        const next = [...prev];
                        const current = next[idx];
                        const enteringEdit = !current.editing;
                        next[idx] = {
                          ...current,
                          editing: enteringEdit,
                          ...(enteringEdit ? { isMemberRateApplied: false } : {}),
                        };
                        return next;
                      })}
                    >
                      <img src={EditIcon} alt="edit" />
                    </button>
                  )}
                </div>
              </div>
              {!it.isVoucher && it.editing && (
                <div className={styles.lineItemRow}>
                  <span className={styles.unitPriceLabel}>Unit price:</span>
                  <input
                    className={styles.priceEditInput}
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.price}
                    disabled={isCheckoutLocked}
                    readOnly={isCheckoutLocked}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setItems((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], price: '' };
                          return next;
                        });
                        return;
                      }
                      const v = parseFloat(value);
                      setItems((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], price: isNaN(v) ? 0 : v };
                        return next;
                      });
                    }}
                    onBlur={(e) => {
                      const relatedTarget = e.relatedTarget;
                      if (relatedTarget && relatedTarget.className && relatedTarget.className.includes('reasonInput')) {
                        return;
                      }
                      setItems((prev) => {
                        const next = [...prev];
                        const currentPrice = next[idx].price;
                        const finalPrice = (currentPrice === '' || isNaN(currentPrice)) ? 0 : parseFloat(currentPrice);
                        next[idx] = { ...next[idx], price: finalPrice, editing: false };
                        return next;
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                    }}
                    autoFocus
                  />
                </div>
              )}
              {it.isVoucher && (
                <div className={styles.voucherCodesSection}>
                  {voucherCodes.map((code, codeIdx) => {
                    const refKey = it.lineId != null ? `${it.lineId}-${codeIdx}` : `idx${idx}-${codeIdx}`;
                    const verifyKey = it.lineId != null ? voucherVerifySlotKey(it.lineId, codeIdx) : '';
                    const vstate = verifyKey ? voucherVerifyByKey[verifyKey] : undefined;
                    const verifyStatus = vstate?.status || 'idle';
                    const hasVoucherCode = String(code || '').trim().length > 0;
                    const showClearVoucherBtn = hasVoucherCode && verifyStatus !== 'loading';
                    return (
                      <div key={`${it.lineId || idx}-voucher-${codeIdx}`} className={styles.voucherCodeRow}>
                        <div className={styles.voucherInputShell}>
                          <input
                            ref={(el) => {
                              voucherScanInputRefs.current[refKey] = el;
                            }}
                            className={`${styles.voucherInput} ${verifyStatus === 'error' ? styles.voucherInputError : ''}`}
                            type="text"
                            placeholder="Enter voucher code"
                            value={code}
                            disabled={isCheckoutLocked || verifyStatus === 'loading'}
                            readOnly={isCheckoutLocked}
                            onChange={(e) => handleVoucherCodeChange(it.lineId, codeIdx, e.target.value, idx)}
                            onBlur={(e) => handleVoucherInputBlur(it.lineId, codeIdx, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter') return;
                              e.preventDefault();
                              if (verifyKey) {
                                skipVoucherVerifyBlurRef.current[verifyKey] = true;
                              }
                              verifyVoucherSerial(it.lineId, codeIdx, e.currentTarget.value);
                            }}
                          />
                          {verifyStatus === 'success' && (
                            <span className={styles.voucherInputTick} title="Verified">
                              <VoucherVerifySuccessIcon />
                            </span>
                          )}
                        </div>
                        {showClearVoucherBtn && !isCheckoutLocked && (
                          <button
                            type="button"
                            className={styles.voucherTrashBtn}
                            title="Clear voucher serial"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleClearVoucherCode(it.lineId, codeIdx, idx)}
                          >
                            <TrashIcon width={18} height={18} color="#767676" />
                          </button>
                        )}
                        {!isCheckoutLocked && verifyStatus !== 'success' && (
                          <button
                            type="button"
                            className={styles.scanBtn}
                            onClick={() => focusVoucherScanInput(it.lineId, idx, codeIdx)}
                          >
                            <VoucherScanIcon size={16} className={styles.scanBtnIcon} />
                            Scan
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Show quantity limit message when reached */}
              {it.availableQuantity !== null && it.qty >= it.availableQuantity && (
                <div className={styles.quantityLimitMessage}>
                  Maximum quantity reached ({it.availableQuantity} available)
                </div>
              )}
              {it.editing && !it.isVoucher && !isCheckoutLocked && (
                <div className={styles.reasonRow}>
                  <input
                    className={styles.reasonInput}
                    placeholder="Write reason"
                    value={it.reason}
                    onChange={(e)=> setItems((prev)=>{ const next=[...prev]; next[idx] = { ...next[idx], reason: e.target.value }; return next; })}
                  />
                </div>
              )}
              {it.quantityError && (
                <div style={{ color: '#f44336', fontSize: '12px', padding: '8px 0', fontStyle: 'italic' }}>
                  {it.quantityError}
                </div>
              )}
              {it.isMemberRateApplied && !it.isVoucher && (
                <div className={styles.memberRateRow}>
                  <div className={dropdownStyles.selectedDiscount}>
                    <span className={dropdownStyles.selectedDiscountText}>Member Rate</span>
                    {!isCheckoutLocked && (
                    <button
                      type="button"
                      className={dropdownStyles.selectedDiscountClose}
                      onClick={() => setItems((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], isMemberRateApplied: false };
                        return next;
                      })}
                      aria-label="Remove Member Rate"
                    >
                      <img src={CrossIcon} alt="" width={10} height={10} />
                    </button>
                    )}
                  </div>
                  <span className={styles.discountValue}>
                    - ${getItemMemberDiscountAmount(it).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
            );
          })}
          {items.length > 0 && !isCheckoutLocked && (
            <div className={styles.addProductRow}>
              {canAddProducts && (
                <div className={styles.addRowAction}>
                  <QuantityCircleAddIcon className={styles.addRowIcon} size={18} color="#767676" aria-hidden />
                  <div className={styles.addRowDropdownWrap}>
                    <CustomDropdown
                      options={productOptions}
                      value={null}
                      onChange={handleAddProduct}
                      placeholder="Add product"
                      disabled={isLoadingProducts || !!productsError || isCheckoutLocked}
                      disableSearch={false}
                      autoFocusSearch
                      searchPlaceholder="Search ..."
                      renderRight={(opt) => opt.right}
                      triggerPaddingX={4}
                      hideUnderline
                      hideIcon
                      compactTrigger
                      triggerColor="var(--neutral-500)"
                      onOpen={scrollToBottom}
                      alignMenuToRow
                      menuClassName="checkoutAddRowDropdownMenu"
                    />
                  </div>
                </div>
              )}
              {canAddVouchers && !hideCheckoutVoucherAddOnStaging && (
                <div className={styles.addRowAction}>
                  <QuantityCircleAddIcon className={styles.addRowIcon} size={18} color="#767676" aria-hidden />
                  <div className={styles.addRowDropdownWrap}>
                    {voucherOptions.some((o) => !o.isHeader) ? (
                      <CustomDropdown
                        options={voucherOptions}
                        value={null}
                        onChange={handleAddProduct}
                        placeholder="Add voucher"
                        disabled={isLoadingVouchers || !!vouchersError}
                        disableSearch={false}
                        autoFocusSearch
                        searchPlaceholder="Search ..."
                        renderRight={(opt) => opt.right}
                        triggerPaddingX={4}
                        hideUnderline
                        hideIcon
                        compactTrigger
                        triggerColor="var(--neutral-500)"
                        onOpen={scrollToBottom}
                        alignMenuToRow
                        menuClassName="checkoutAddRowDropdownMenu"
                      />
                    ) : (
                      <button
                        type="button"
                        className={styles.addRowStaticMuted}
                        disabled={isLoadingVouchers || !!vouchersError}
                        onClick={() => {
                          if (!isLoadingVouchers && !vouchersError) {
                            showAppToast('info', 'No vouchers available.');
                          }
                        }}
                      >
                        Add voucher
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes Section — editable even during partial payment */}
          {items.length > 0 && (
            <div className={styles.notesSection}>
              <div className={styles.fieldRow}>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note (Optional)"
                  className={styles.noteInput}
                />
              </div>
            </div>
          )}
        </div>
        )}

        {step !== 'confirmation' && step !== 'payment' && isCheckoutLocked && showPaymentInProgressNotice && (
          <div className={styles.paymentInProgressNotice}>
            {PAYMENT_IN_PROGRESS_MESSAGE}
          </div>
        )}

        {step !== 'confirmation' && step !== 'payment' && (
          <div className={styles.footer}>
            {customerApiError && (
              <div className={styles.customerApiErrorContainer}>
                <div className={styles.customerApiError}>{customerApiError}</div>
              </div>
            )}
            {items.length > 0 && (
              <div className={styles.subtotalRow}>
                <span className={styles.subtotalLabel}>Subtotal</span>
                <span className={styles.subtotalValue}>${productDiscountedSubtotal.toFixed(2)}</span>
              </div>
            )}
            <button
              className={styles.primaryCta}
              disabled={
                !isCustomerSelectedLocal ||
                items.length === 0 ||
                items.some((it) => !it.isVoucher && it.quantityError)
              }
              title={
                !isCustomerSelectedLocal ? 'Please select a customer' :
                items.length === 0 ? 'Please add at least one product' :
                items.some((it) => !it.isVoucher && it.quantityError) ? 'Please fix quantity errors before proceeding' :
                'Go to Payments'
              }
              onClick={() => {
                const outletId = selectedOutlet || loggedOutletID;
                let hasError = false;

                for (const it of items) {
                  if (!it.isVoucher) continue;
                  const codes = normalizeVoucherCodes(it.voucherCodes, it.qty);
                  for (let codeIdx = 0; codeIdx < codes.length; codeIdx += 1) {
                    const serial = String(codes[codeIdx] || '').trim();
                    if (!serial) {
                      showAppToast('error', 'Enter and verify all voucher codes before proceeding.');
                      return;
                    }
                    const verifyKey = it.lineId ? voucherVerifySlotKey(it.lineId, codeIdx) : '';
                    const verifyStatus = verifyKey ? voucherVerifyByKey[verifyKey]?.status : '';
                    if (verifyStatus !== 'success') {
                      showAppToast('error', 'Verify all voucher codes before proceeding.');
                      return;
                    }
                  }
                }
                
                const validatedItems = items.map((it) => {
                  if (it.isVoucher) {
                    return { ...it, quantityError: null, availableQuantity: null };
                  }
                  const availableQty = it.availableQuantity !== null 
                    ? it.availableQuantity 
                    : getAvailableQuantity(it.productId, outletId);
                  
                  if (availableQty !== null && it.qty > availableQty) {
                    hasError = true;
                    return { ...it, quantityError: `Only ${availableQty} available` };
                  }
                  return { ...it, quantityError: null };
                });
                
                if (hasError) {
                  setItems(validatedItems);
                  showAppToast('error','Please fix quantity errors before proceeding');
                  return;
                }
                
                if (!isCheckoutLocked) {
                  setAmountDue(productGrandTotal.toFixed(2));
                }
                setStep('payment');
              }}
            >
              {isCheckoutLocked ? 'Continue Payment' : 'Go to Payments'}
            </button>
          </div>
        )}
      </div>
      {isCheckoutTopupOpen && (
        <CustomerDetailsModal
          key={`topup-${checkoutTopupCustomer?.id ?? resolveTopupUserId() ?? 'checkout'}`}
          customer={checkoutTopupCustomer ?? buildTopupCheckoutCustomer()}
          isOpen={isCheckoutTopupOpen}
          onClose={() => {
            restoreCheckoutPaymentMethodAfterTopup();
            checkoutTopupTargetCustomerRef.current = null;
            setCheckoutTopupCustomer(null);
            setCheckoutRecoveryMembershipSale(null);
            setIsOthersMemberTopupFlow(false);
            setIsCheckoutTopupOpen(false);
          }}
          autoOpenMembershipFlow={checkoutRecoveryMembershipSale ? null : checkoutMembershipFlowType}
          recoveryMembershipSale={checkoutRecoveryMembershipSale}
          membershipFlowOnly={true}
          membershipFromNewCheckout={true}
          skipSetMembershipPasswordAfterPayment={isOthersMemberTopupFlow}
          onMembershipPaymentSuccess={async (detail) => {
            setCheckoutTopupRefreshKey((previousValue) => previousValue + 1);
            const topupTargetClient = checkoutTopupTargetCustomerRef.current;
            const topupUserId = resolveTopupUserId();
            if (topupTargetClient?.user_id) {
              const patch = await fetchMembershipClientCreditPatch(topupUserId);
              if (patch) {
                setOthersMembershipClient((previous) => ({
                  ...(previous || topupTargetClient),
                  ...patch,
                }));
              }
            } else {
              await refreshCustomerCreditDetailsFromUser(
                topupUserId,
                setCustomerCreditDetails
              );
            }
            if (detail && detail.restoreCheckoutTotals === false) {
              return;
            }
            restoreCheckoutPaymentMethodAfterTopup();
            if (!detail?.stayOnCheckoutStep) {
              return;
            }
            setServiceTotal(productDiscountedSubtotal);
            const remainingDue = computeRemainingCheckoutAmount(
              productGrandTotal,
              paymentHistory,
              checkoutAmountsBeforeTopupRef.current.amountDue
            );
            const remainingDueStr = remainingDue.toFixed(2);
            setAmountDue(remainingDueStr);
            const preservedCharge = parseFloat(
              checkoutAmountsBeforeTopupRef.current.amountToCharge
            );
            const chargeAmount =
              Number.isFinite(preservedCharge) && preservedCharge > 0
                ? Math.min(preservedCharge, remainingDue)
                : remainingDue;
            setAmountToCharge(chargeAmount.toFixed(2));
            const returnStep = checkoutStepBeforeTopupRef.current || 'confirmation';
            setStep(
              returnStep === 'payment' || returnStep === 'confirmation'
                ? returnStep
                : 'confirmation'
            );
          }}
        />
      )}
    </div>
  );
};

export default CheckoutSidebar;
